// OpsAccountsHost — the impure shell edge of Ops › Accounts
// (`/ops/accounts`). It owns the accounts read (on open, on Try again, and
// after every action), the four actions (create, disable, enable, delete:
// one server route each) and what the last one did, and the frame; the
// pure `OpsAccountsPage` gets them as props.

export { OpsAccountsHost };

import type { OpsAccount } from "@bilda/app/ops";
import { Result } from "@bilda/utils";
import { useState } from "react";
import {
    type AccountsNotice,
    OpsAccountsPage,
} from "../../components/Ops/OpsAccountsPage.js";
import { TooltipProvider } from "../../components/Tooltip/Tooltip.js";
import { type OpsActionFailure, opsApi } from "./api.js";
import { isDenied, useOpsFrame, useRemote } from "./hooks.js";

const OpsAccountsHost = () => {
    const frame = useOpsFrame();
    const [attempt, setAttempt] = useState(0);
    const accounts = useRemote(opsApi.accounts, attempt);
    const [notice, setNotice] = useState<AccountsNotice>();
    const [busy, setBusy] = useState(false);

    const run = (
        action: Promise<Result.Result<void, OpsActionFailure>>,
        onDone: AccountsNotice | undefined,
    ) => {
        setBusy(true);
        setNotice(undefined);
        action.then((r) => {
            setBusy(false);
            setNotice(
                Result.isOk(r)
                    ? onDone
                    : r.error.type === "ops_refused"
                      ? { type: "refused", reason: r.error.reason }
                      : { type: "failed" },
            );
            setAttempt((n) => n + 1);
        });
    };

    return (
        <TooltipProvider>
            <OpsAccountsPage
                frame={frame}
                access={
                    accounts._tag === "Remote.Failed" &&
                    isDenied(accounts.error)
                        ? "denied"
                        : "allowed"
                }
                accounts={accounts}
                notice={notice}
                busy={busy}
                onRetry={() => setAttempt((n) => n + 1)}
                onCreate={(email) =>
                    run(opsApi.createAccount(email), { type: "created", email })
                }
                onDisable={(a: OpsAccount) =>
                    run(opsApi.disableAccount(a.id), undefined)
                }
                onEnable={(a: OpsAccount) =>
                    run(opsApi.enableAccount(a.id), undefined)
                }
                onRemove={(a: OpsAccount, confirmEmail: string) =>
                    run(opsApi.removeAccount(a.id, confirmEmail), undefined)
                }
            />
        </TooltipProvider>
    );
};
